import type { Entity } from 'koota';
import { emit, getContext } from '../context';
import { impactMaterial } from '../level/material';
import { raycastClosest, type RayHit } from '../physics/queries';
import { shieldBlocks } from '../player/combat';
import { takeDamage } from '../player/health';
import { knockbackScale } from '../rules/party';
import { Dead, Hazard, HazardKind, Health, Player, RoundPhase, RoundState, Static } from '../traits';
import { cannonBall, cannonPhase, CANNON_WARNING_TICKS } from './cannonTiming';
import { spawnHazardEntity } from './common';
import type { HazardModule } from './types';

/** Straight, guardable cannon fire. Balls stop on cover and never accumulate physics bodies. */
export const cannon: HazardModule = {
  typeId: 'cannon',
  kind: HazardKind.Cannon,
  create(world, obj) {
    const entity = spawnHazardEntity(world, obj, HazardKind.Cannon);
    entity.add(Static());
    entity.set(Hazard, { ...entity.get(Hazard)!, hp: 0, armed: 0 });
    return entity;
  },
  step(world, entity, hz, tr) {
    const ctx = getContext(world);
    if (world.get(RoundState)?.phase !== RoundPhase.Fighting) {
      // Mutate the query view too: hazardsStep uses it for warning transitions and writes it back.
      hz.hp = 0;
      hz.armed = 0;
      entity.set(Hazard, hz);
      return;
    }
    // Store the live round's origin plus one; countdown never consumes the warning.
    if (!hz.hp) hz.hp = ctx.tick + 1;
    const phase = cannonPhase(hz, ctx.tick);
    const firing = phase === CANNON_WARNING_TICKS;
    if (firing) hz.armed = 1;
    const ball = cannonBall(hz, tr.x, tr.y, ctx.tick);
    if (!ball) hz.armed = 0;
    if (ball) {
      const dir = hz.param3 < 0 ? -1 : 1;
      const previousX = firing ? tr.x : ball.x - ball.vx / 60;
      if (firing) emit(world, { type: 'cannon-shot', x: ball.x, y: ball.y, dir });
      let nearest: RayHit | null = null;
      for (const offset of [0, -0.18, 0.18]) {
        const hit = raycastClosest(world, previousX, ball.y + offset, ball.x, ball.y + offset, (h) => {
          if (h.fixture.isSensor() || !['solid', 'prop', 'player'].includes(h.kind)) return true;
          const target = h.entity as Entity;
          return !world.has(target) || target.has(Dead) || (target.has(Player) && (target.get(Health)?.hp ?? 0) <= 0);
        });
        if (hit && (!nearest || hit.fraction < nearest.fraction)) nearest = hit;
      }
      if (nearest) {
        hz.armed = 0;
        const target = nearest.entity as Entity;
        if (target.has(Player)) {
          if (shieldBlocks(world, target, previousX, ball.y) !== 'none') {
            emit(world, { type: 'block', player: target, reflected: false, x: nearest.x, y: nearest.y });
          } else if (takeDamage(world, target, 35, 'body', -1, nearest.x, nearest.y) > 0) {
            const body = ctx.bodies.get(target), velocity = body?.getLinearVelocity();
            if (body && velocity) body.setLinearVelocity({ x: velocity.x + dir * 10 * knockbackScale(ctx.settings), y: velocity.y + 2 });
          }
        }
        emit(world, { type: 'impact', x: nearest.x, y: nearest.y, nx: nearest.nx, ny: nearest.ny, material: impactMaterial(target.get(Hazard)?.kind, ctx.level.theme) });
      }
    }
    entity.set(Hazard, hz);
  },
};
